import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CloseOutlined, ReloadOutlined, ArrowLeftOutlined, HomeOutlined, TranslationOutlined } from '@ant-design/icons';
import { getPublicChatbotFlows, ChatbotLang, PublicChatOption } from './publicChatbotFlows';
import { getblobLink } from '../../services/helper';

const chatbotbg = `${getblobLink()}/public/asset/chatbotBg.webp`;
const chatbot = `${getblobLink()}/public/asset/chatbot.png`;

const IS_HOME = 'start';

const PublicChatbot: React.FC = () => {

  const [isOpen, setIsOpen] = useState(false);
  const [lang, setLang] = useState<ChatbotLang>('en');
  const [currentNode, setCurrentNode] = useState(IS_HOME);
  const [messages, setMessages] = useState<Array<{ type: 'bot' | 'user'; text: string }>>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [position, setPosition] = useState({
    xPercent: ((window.innerWidth - 100) / window.innerWidth) * 100,
    yPercent: ((window.innerHeight - 100) / window.innerHeight) * 100,
  });
  const pixelPosition = {
    x: (position.xPercent / 100) * window.innerWidth,
    y: (position.yPercent / 100) * window.innerHeight,
  };
  const BUTTON_SIZE = 56;
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const dragStartPos = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  const flows = getPublicChatbotFlows(lang);

  // Init welcome message
  useEffect(() => {
    if (messages.length === 0) {
      const node = flows[IS_HOME];
      if (node) setMessages([{ type: 'bot', text: node.message }]);
    }
  }, []);

  // Scroll to latest
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const addBotMessage = useCallback((text: string) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, { type: 'bot', text }]);
    }, 500);
  }, []);

  // Language toggle handler
  const handleLanguageToggle = useCallback(() => {
    const newLang: ChatbotLang = lang === 'en' ? 'ta' : 'en';
    setLang(newLang);
    const newFlows = getPublicChatbotFlows(newLang);
    setCurrentNode(IS_HOME);
    setHistory([]);
    const node = newFlows[IS_HOME];
    if (node) setMessages([{ type: 'bot', text: node.message }]);
  }, [lang]);

  const handleOptionSelect = useCallback((option: PublicChatOption) => {
    setMessages(prev => [...prev, { type: 'user', text: `${option.icon ? option.icon + ' ' : ''}${option.label}` }]);

    // ── Language switch ──────────────────────────────────────────────────
    if (option.setLang) {
      const newLang = option.setLang;
      const newFlows = getPublicChatbotFlows(newLang);
      setLang(newLang);
      setHistory([]);
      // Use option.next if it exists in the new flows, otherwise fall back to IS_HOME
      const targetNode = (option.next && newFlows[option.next]) ? option.next : IS_HOME;
      setCurrentNode(targetNode);
      const node = newFlows[targetNode];
      if (node) addBotMessage(node.message);
      return;
    }

    // Action-based navigation
    if (option.action) {
      const currentFlows = getPublicChatbotFlows(lang);
      switch (option.action) {
        case 'register':
          addBotMessage(lang === 'ta' ? "அருமை! இப்போது பதிவு பக்கத்திற்கு அழைத்துச் செல்கிறேன் 🚀" : "Great! Taking you to the registration page now 🚀");
          setTimeout(() => navigate('/register'), 900);
          return;
        case 'login':
          addBotMessage(lang === 'ta' ? "உள்நுழைவு பக்கத்திற்கு அழைத்துச் செல்கிறேன்..." : "Taking you to the login page...");
          setTimeout(() => navigate('/login'), 900);
          return;
        case 'pricing':
          addBotMessage(lang === 'ta' ? "விலை பக்கத்தை உங்களுக்காக திறக்கிறேன் 💰" : "Opening the pricing page for you 💰");
          setTimeout(() => navigate('/pricing'), 900);
          return;
        case 'contact':
          addBotMessage(lang === 'ta' ? "தொடர்பு பகுதிக்கு செல்கிறேன்..." : "Scrolling to the contact section...");
          setTimeout(() => {
            // If we're not on the landing page, navigate there first then scroll
            if (location.pathname !== '/') {
              navigate('/');
              // Wait for the landing page to render, then scroll
              setTimeout(() => {
                const el = document.getElementById('contact');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }, 500);
            } else {
              const el = document.getElementById('contact');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }
            setIsOpen(false);
          }, 900);
          return;
      }
    }

    // Node navigation
    if (option.next) {
      const currentFlows = getPublicChatbotFlows(lang);
      setHistory(prev => [...prev, currentNode]);
      const nextNode = currentFlows[option.next];
      if (nextNode) {
        setCurrentNode(option.next);
        addBotMessage(nextNode.message);
      }
    }
  }, [currentNode, navigate, addBotMessage, lang, location.pathname]);

  const handleBack = useCallback(() => {
    if (history.length > 0) {
      const prevNode = history[history.length - 1];
      setHistory(prev => prev.slice(0, -1));
      setCurrentNode(prevNode);
      const currentFlows = getPublicChatbotFlows(lang);
      const node = currentFlows[prevNode];
      if (node) {
        setMessages(prev => [...prev, { type: 'user', text: '← Back' }]);
        addBotMessage(node.message);
      }
    }
  }, [history, addBotMessage, lang]);

  const handleRestart = useCallback(() => {
    setCurrentNode(IS_HOME);
    setHistory([]);
    const currentFlows = getPublicChatbotFlows(lang);
    const node = currentFlows[IS_HOME];
    if (node) setMessages([{ type: 'bot', text: node.message }]);
  }, [lang]);

  const currentOptions = flows[currentNode]?.options || [];
  const isHome = currentNode === IS_HOME;

  const nodeLabelMap: Record<string, string> = {
    start: 'Home', what_is: 'About', features: 'Features',
    pricing: 'Pricing', free_trial: 'Free Trial', get_started: 'Get Started',
    mobile_app: 'Mobile App', contact: 'Contact', faq: 'FAQ',
    pricing_recommend: 'Find Plan', feat_enrollment: 'Enrollment',
    feat_batches: 'Batches', feat_vehicles: 'Vehicles',
    feat_accounting: 'Accounting', feat_users: 'Users', feat_dashboard: 'Dashboard',
    plan_standard: 'Standard', plan_professional: 'Professional', plan_enterprise: 'Enterprise',
    rec_small: 'Small School', rec_medium: 'Growing School', rec_large: 'Large School',
    faq_security: 'Security', faq_branches: 'Branches', faq_documents: 'Documents', faq_export: 'Exports',
  };

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragging.current = true;
    hasMoved.current = false;
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    offset.current = {
      x: e.clientX - pixelPosition.x,
      y: e.clientY - pixelPosition.y,
    };
  }, [pixelPosition]);

  const clampPositionToViewport = useCallback((x: number, y: number, width: number, height: number) => {
    const viewportW = window.innerWidth || 0;
    const viewportH = window.innerHeight || 0;
    const maxX = Math.max(viewportW - width, 0);
    const maxY = Math.max(viewportH - height, 0);
    return {
      x: Math.min(Math.max(x, 0), maxX),
      y: Math.min(Math.max(y, 0), maxY),
    };
  }, []);

  const getWindowPosition = useCallback(() => {
    const winWidth = 370;
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const maxHeight = Math.min(560, viewportH - 120);
    const winHeight = maxHeight;
    const btnSize = 56;
    const margin = 10;

    let left = pixelPosition.x - winWidth + btnSize;
    let top = pixelPosition.y - winHeight - margin;

    if (top < 8) top = pixelPosition.y + btnSize + margin;

    const clamped = clampPositionToViewport(left, top, winWidth, winHeight);
    const safeLeft = Math.min(Math.max(clamped.x, 8), Math.max(viewportW - winWidth - 8, 8));
    const safeTop = Math.min(Math.max(clamped.y, 8), Math.max(viewportH - winHeight - 8, 8));

    return { left: safeLeft, top: safeTop, winHeight };
  }, [pixelPosition, clampPositionToViewport]);

  const clampPosition = useCallback((x: number, y: number) => {
    const viewportW = window.innerWidth || 0;
    const viewportH = window.innerHeight || 0;
    const { x: clampedX, y: clampedY } = clampPositionToViewport(x, y, BUTTON_SIZE, BUTTON_SIZE);
    return {
      xPercent: viewportW > 0 ? (clampedX / viewportW) * 100 : 0,
      yPercent: viewportH > 0 ? (clampedY / viewportH) * 100 : 0,
    };
  }, [BUTTON_SIZE, clampPositionToViewport]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragging.current) return;
    const dx = Math.abs(e.clientX - dragStartPos.current.x);
    const dy = Math.abs(e.clientY - dragStartPos.current.y);
    if (dx > 5 || dy > 5) hasMoved.current = true;
    if (!hasMoved.current) return;
    e.preventDefault();
    const newX = e.clientX - offset.current.x;
    const newY = e.clientY - offset.current.y;
    setPosition(clampPosition(newX, newY));
  }, [clampPosition]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    dragging.current = false;
  }, []);

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (hasMoved.current) {
      e.preventDefault();
      e.stopPropagation();
      hasMoved.current = false;
      return;
    }
    setIsOpen(true);
  }, []);

  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('open-chatbot', handler);
    return () => window.removeEventListener('open-chatbot', handler);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const pixelX = (position.xPercent / 100) * window.innerWidth;
      const pixelY = (position.yPercent / 100) * window.innerHeight;
      setPosition(clampPosition(pixelX, pixelY));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [position.xPercent, position.yPercent, clampPosition]);

  return (
    <>
      {/* ── Floating Launcher ── */}
      {!isOpen && (
        <button className="pcb-launcher" onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp}
          onClick={handleClick} style={{ 
            position: "fixed", 
            left: `${pixelPosition.x}px`, 
            top: `${pixelPosition.y}px`, 
            cursor: dragging.current ? 'grabbing' : 'grab', 
            userSelect: 'none', 
            touchAction: 'none', 
          }}
          aria-label="Open help assistant">
          <img src={chatbot} alt="DriveMynt Assistant" className="pcb-launcher-icon" draggable={false} />
          <span className="pcb-launcher-ring" />
        </button>
      )}

      {/* ── Chat Window ── */}
      {isOpen && (
        <div className="pcb-window" style={{
            position: "fixed",
            zIndex: 9999,
            left: `${getWindowPosition().left}px`,
            top: `${getWindowPosition().top}px`,
            bottom: 'unset',
            right: 'unset',
            width: '370px',
            maxHeight: `${getWindowPosition().winHeight}px`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>

          {/* Header */}
          <div className="pcb-header">
            <div className="pcb-header-brand">
              <div className="pcb-header-avatar">
                <img src={chatbotbg} alt="DriveMynt" className="pcb-avatar-img" />
              </div>
              <div>
                <div className="pcb-header-name">DriveMynt Assistant</div>
                <div className="pcb-header-status">
                  <span className="pcb-status-dot" />
                  Online — here to help
                </div>
              </div>
            </div>
            <div className="pcb-header-controls">
              {!isHome && (
                <button className="pcb-ctrl-btn" onClick={handleRestart} title="Home">
                  <HomeOutlined />
                </button>
              )}
              <button className="pcb-ctrl-btn" onClick={handleRestart} title="Restart">
                <ReloadOutlined />
              </button>
              <button className="pcb-ctrl-btn pcb-ctrl-close" onClick={() => setIsOpen(false)} title="Close">
                <CloseOutlined />
              </button>
            </div>
          </div>

          {/* Breadcrumb — shows only: Home › Current */}
          {history.length > 0 && (
            <div className="pcb-breadcrumb">
              <span className="pcb-breadcrumb-item">Home</span>
              <span className="pcb-breadcrumb-sep">›</span>
              <span className="pcb-breadcrumb-item pcb-breadcrumb-current">
                {nodeLabelMap[currentNode] ?? currentNode.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
            </div>
          )}

          {/* Messages */}
          <div className="pcb-messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`pcb-msg ${msg.type === 'bot' ? 'pcb-msg-bot' : 'pcb-msg-user'}`}>
                {msg.type === 'bot' && (
                  <div className="pcb-msg-avatar">
                    <img src={chatbotbg} alt="Bot" className="pcb-avatar-img" />
                  </div>
                )}
                <div className={`pcb-bubble ${msg.type === 'bot' ? 'pcb-bubble-bot' : 'pcb-bubble-user'}`}>
                  {msg.text.split('\n').map((line, i, arr) => (
                    <React.Fragment key={i}>
                      {line}{i < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="pcb-msg pcb-msg-bot">
                <div className="pcb-msg-avatar">
                  <img src={chatbot} alt="Bot" className="pcb-avatar-img" />
                </div>
                <div className="pcb-bubble pcb-bubble-bot pcb-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Options */}
          {!isTyping && currentOptions.length > 0 && (
            <div className="pcb-options-panel">
              {history.length > 0 && (
                <button className="pcb-opt-back" onClick={handleBack}>
                  <ArrowLeftOutlined /> Back
                </button>
              )}

              {isHome ? (
                <div className="pcb-home-grid">
                  {currentOptions.map((opt, i) => (
                    <button
                      key={i}
                      className="pcb-home-card"
                      onClick={() => handleOptionSelect(opt)}
                    >
                      <span className="pcb-home-card-icon">{opt.icon}</span>
                      <span className="pcb-home-card-label">{opt.label}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="pcb-opt-list">
                  {currentOptions.map((opt, i) => (
                    <button
                      key={i}
                      className={`pcb-opt-item ${opt.action === 'register' ? 'pcb-opt-cta' : ''} ${opt.label.startsWith('Back') ? 'pcb-opt-nav' : ''}`}
                      onClick={() => handleOptionSelect(opt)}
                    >
                      {opt.icon && <span className="pcb-opt-icon">{opt.icon}</span>}
                      <span className="pcb-opt-label">{opt.label}</span>
                      {opt.action === 'register' && <span className="pcb-opt-cta-tag">Free</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="pcb-footer">
            <span>DriveMynt — Smart Driving School Platform</span>
          </div>
        </div>
      )}
    </>
  );
};

export default PublicChatbot;
